import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SiteNav from "@/components/layout/SiteNav";
import SiteFooter from "@/components/layout/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SectionLabel, PillBadge } from "@/components/common/Bits";
import MatchRing from "@/components/common/MatchRing";
import { ArrowRight, Sparkles, Wallet, Receipt, ShieldCheck, Check, Plus, FileSignature, Inbox, Search, Hourglass, HandCoins, Star, Landmark, XCircle, CheckCircle2, ArrowUpRight } from "lucide-react";
import { FAQ_ITEMS, PUBLIC_SAMPLE_GRANTS, SETTLEMENTS, STATS, TESTIMONIALS, formatMoney } from "@/lib/mockData";

const HERO_IMG = "https://images.pexels.com/photos/8970649/pexels-photo-8970649.jpeg";
const LIFESTYLE_1 = "https://images.pexels.com/photos/7318931/pexels-photo-7318931.jpeg";
const LIFESTYLE_2 = "https://images.unsplash.com/photo-1752659504296-16a5fd9fa86a?auto=format&fit=crop&w=1200";
const COINS_IMG = "https://images.unsplash.com/photo-1755369360283-c05697b73692?auto=format&fit=crop&w=800";

const AGENCIES = ["NSF", "HUD", "SBA", "NIH", "USDA", "DOE", "VA", "NEA", "IRS", "ED", "MBDA", "ACF", "PBGC", "TreasuryDirect"];

export default function Landing() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteNav />
            <Hero />
            <Marquee />
            <Categories />
            <HowItWorks />
            <SampleMatches />
            <Stats />
            <TrustSection />
            <Testimonials />
            <FAQSection />
            <FinalCTA />
            <SiteFooter />
        </div>
    );
}

function Hero() {
    const [counter, setCounter] = useState(0);
    useEffect(() => {
        const target = 12847;
        const start = Date.now();
        const duration = 1600;
        let raf;
        const tick = () => {
            const t = Math.min(1, (Date.now() - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setCounter(Math.round(eased * target));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <section className="relative overflow-hidden">
            {/* soft backdrop */}
            <div aria-hidden className="absolute inset-0 bg-gradient-hero" />
            <div aria-hidden className="absolute top-20 -left-40 h-[420px] w-[420px] rounded-full bg-accent/12 blur-[120px]" />
            <div aria-hidden className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-primary/10 blur-[140px]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-14 pb-20 lg:pt-24 lg:pb-32">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <PillBadge tone="accent">
                            <Sparkles className="h-3.5 w-3.5" /> New · 1,400+ federal & state programs indexed
                        </PillBadge>

                        <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[76px] leading-[1.02] tracking-tight text-balance">
                            Claim what's
                            <br />
                            <span className="italic text-primary">rightfully</span> yours.
                        </h1>
                        <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed text-pretty">
                            Owed scans grants, unclaimed property, missed tax credits, and active settlements — matches your profile, drafts the paperwork, and walks you through filing. Real programs. Real dollars. No nonsense.
                        </p>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button asChild size="lg" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-7 text-base shadow-elegant">
                                <Link to="/onboarding">Start my free scan <ArrowRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 text-base border-foreground/20 bg-background">
                                <a href="#how">See how it works</a>
                            </Button>
                        </div>

                        <div className="mt-10 flex items-center gap-5 text-sm text-muted-foreground">
                            <div className="flex -space-x-2">
                                {["MT", "PR", "DW", "JO"].map((i, idx) => (
                                    <span key={i} className="h-8 w-8 rounded-full border-2 border-background bg-primary text-primary-foreground inline-flex items-center justify-center text-[10px] font-semibold" style={{ zIndex: 10 - idx }}>{i}</span>
                                ))}
                            </div>
                            <div className="leading-snug">
                                <p className="text-foreground"><span className="font-mono tabular-nums">{counter.toLocaleString()}</span>+ claims surfaced this week</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                    {[0,1,2,3,4].map(i => <Star key={i} className="h-3 w-3 fill-accent text-accent" />)}
                                    <span className="ml-1">4.9 · 2,184 reviews</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visual column */}
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-elegant">
                            <img src={HERO_IMG} alt="Editorial still life with documents and warm light" className="absolute inset-0 h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent" />
                        </div>

                        {/* Floating cards */}
                        <div className="absolute -top-6 -left-8 hidden sm:block animate-float">
                            <Card className="w-[240px] p-4 border-border shadow-elegant bg-card/95 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">New match</p>
                                        <p className="font-serif text-lg mt-0.5">Amber Grant</p>
                                    </div>
                                    <MatchRing score={94} size={44} />
                                </div>
                                <p className="text-sm text-foreground mt-2">Up to <span className="font-serif text-2xl">$10,000</span></p>
                                <div className="mt-2 flex gap-1">
                                    <Badge variant="outline" className="text-[10px] bg-accent/10 border-accent/40 text-accent-foreground">women-owned</Badge>
                                </div>
                            </Card>
                        </div>

                        <div className="absolute -bottom-6 -right-4 hidden sm:block" style={{ animation: "float-y 6s ease-in-out infinite", animationDelay: "1s" }}>
                            <Card className="w-[260px] p-4 border-border shadow-elegant bg-card/95 backdrop-blur">
                                <div className="flex items-center gap-2">
                                    <span className="h-8 w-8 rounded-full bg-success/15 text-success inline-flex items-center justify-center">
                                        <HandCoins className="h-4 w-4" />
                                    </span>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Unclaimed property</p>
                                </div>
                                <p className="font-serif text-2xl mt-2">$1,247.83</p>
                                <p className="text-xs text-muted-foreground mt-1">NY Comptroller · dormant 2019 account</p>
                                <Button size="sm" className="mt-3 w-full h-8 rounded-full bg-foreground text-background hover:bg-foreground/90">Claim now</Button>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Marquee() {
    return (
        <section className="border-y border-border bg-secondary/40 overflow-hidden">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex items-center gap-10">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap shrink-0">We index programs from</p>
                <div className="flex-1 overflow-hidden relative">
                    <div className="flex gap-10 animate-marquee whitespace-nowrap">
                        {[...AGENCIES, ...AGENCIES].map((a, i) => (
                            <span key={i} className="font-mono text-sm text-muted-foreground tracking-wider">{a}</span>
                        ))}
                    </div>
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-secondary to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-secondary to-transparent" />
                </div>
            </div>
        </section>
    );
}

function Categories() {
    const items = [
        { icon: Sparkles, title: "Grants", count: "1,400+ programs", tone: "primary", img: LIFESTYLE_2, body: "Non-dilutive grants for small business, education, housing, research, arts, and community work.", highlight: formatMoney(275000) },
        { icon: Wallet, title: "Unclaimed property", count: "53 state databases", tone: "accent", img: COINS_IMG, body: "Dormant bank accounts, insurance refunds, utility deposits, matured bonds — waiting for you.", highlight: "$73B outstanding" },
        { icon: Receipt, title: "Missed tax credits", count: "3 prior years", tone: "terracotta", img: LIFESTYLE_1, body: "EITC, Saver's, American Opportunity, clean energy — credits you may have missed on past returns.", highlight: "Avg $2,400" },
        { icon: Landmark, title: "Class action settlements", count: "80+ active", tone: "primary", img: LIFESTYLE_2, body: "Live settlements — data breaches, consumer claims, financial services. Auto-filed claims where possible.", highlight: "Open now" },
    ];
    return (
        <section id="programs" className="relative py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-2xl">
                    <SectionLabel>What we find</SectionLabel>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-balance">
                        Four quiet rivers of money
                        <span className="italic text-primary"> you're entitled to.</span>
                    </h2>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                        We look where most people don't — and in most places you didn't know to look.
                    </p>
                </div>

                <div className="mt-12 grid md:grid-cols-2 gap-5">
                    {items.map((it, idx) => (
                        <Card key={it.title} className="group relative overflow-hidden border-border shadow-soft hover:shadow-elegant transition-shadow">
                            <div className="grid grid-cols-5">
                                <div className="col-span-3 p-7 flex flex-col">
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${it.tone === "accent" ? "bg-accent text-accent-foreground" : it.tone === "terracotta" ? "bg-terracotta text-primary-foreground" : "bg-primary text-primary-foreground"}`}>
                                            <it.icon className="h-4 w-4" />
                                        </span>
                                        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{String(idx + 1).padStart(2, "0")} / 04</span>
                                    </div>
                                    <h3 className="mt-5 font-serif text-3xl tracking-tight">{it.title}</h3>
                                    <p className="mt-2 text-xs text-muted-foreground font-mono uppercase tracking-wider">{it.count}</p>
                                    <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{it.body}</p>
                                    <div className="mt-auto pt-6 flex items-center justify-between">
                                        <p className="font-serif text-xl text-foreground">{it.highlight}</p>
                                        <span className="inline-flex items-center gap-1 text-sm text-foreground group-hover:gap-2 transition-all">
                                            Explore <ArrowUpRight className="h-4 w-4" />
                                        </span>
                                    </div>
                                </div>
                                <div className="col-span-2 relative">
                                    <img src={it.img} alt={it.title} className="absolute inset-0 h-full w-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-br from-background/0 to-primary/30 mix-blend-multiply" />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    const steps = [
        { icon: FileSignature, title: "Tell us who you are", body: "A 3-minute profile: where you live, what you do, your status. Used only to match programs — never sold.", num: "01" },
        { icon: Search, title: "We scan every source", body: "Federal & state grants, unclaimed property registries, missed credits from prior filings, and active settlements.", num: "02" },
        { icon: Hourglass, title: "Prioritized matches", body: "Each match is scored for eligibility, effort, and dollar impact. Easy wins first, deep grants on your schedule.", num: "03" },
        { icon: Inbox, title: "Guided filing", body: "We pre-fill forms, draft narratives, and hand you a clean packet ready to sign and send to the agency.", num: "04" },
    ];
    return (
        <section id="how" className="relative py-20 lg:py-28 bg-secondary/40 border-y border-border">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div className="max-w-2xl">
                        <SectionLabel>How it works</SectionLabel>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-balance">
                            From “I had no idea” to <span className="italic text-primary">claim submitted</span> — in an afternoon.
                        </h2>
                    </div>
                    <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-6">
                        <Link to="/onboarding">Start your profile</Link>
                    </Button>
                </div>

                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {steps.map((s, i) => (
                        <div key={s.num} className="relative flex flex-col gap-4 p-6 rounded-2xl bg-card border border-border shadow-soft">
                            <div className="flex items-start justify-between">
                                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                                    <s.icon className="h-5 w-5" />
                                </span>
                                <span className="font-serif text-4xl text-muted-foreground/30">{s.num}</span>
                            </div>
                            <h3 className="font-serif text-xl leading-tight">{s.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                            {i < steps.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-border" />}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function SampleMatches() {
    const top = PUBLIC_SAMPLE_GRANTS;
    return (
        <section className="relative py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div className="max-w-2xl">
                        <SectionLabel>Live sample</SectionLabel>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-balance">
                            A peek at what a real <span className="italic text-primary">match report</span> looks like.
                        </h2>
                        <p className="mt-4 text-base text-muted-foreground leading-relaxed">Your actual results are tailored to your profile. Below is a composite example — the quality and clarity are what you'll get.</p>
                    </div>
                </div>

                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {top.map((g) => (
                        <article key={g.id} className="group flex flex-col h-full bg-card border border-border rounded-2xl p-6 shadow-soft hover:shadow-elegant transition-shadow">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{g.orgShort} · {g.category}</p>
                                    <h3 className="font-serif text-xl mt-2 leading-tight">{g.title}</h3>
                                </div>
                                <MatchRing score={g.matchScore} size={48} />
                            </div>
                            <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-2">{g.description}</p>
                            <div className="mt-5 flex items-end justify-between pt-4 border-t border-border mt-auto">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Up to</p>
                                    <p className="font-serif text-2xl">{formatMoney(g.amount, { compact: true })}</p>
                                </div>
                                <span className="inline-flex items-center gap-1 text-sm font-medium">Details <ArrowUpRight className="h-4 w-4" /></span>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-10 text-center">
                    <Button asChild size="lg" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-7">
                        <Link to="/onboarding">See my matches <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                </div>
            </div>
        </section>
    );
}

function Stats() {
    return (
        <section className="relative py-20 lg:py-24 bg-primary text-primary-foreground overflow-hidden">
            <div aria-hidden className="absolute inset-0 opacity-[0.09]" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, hsl(var(--accent)) 0, transparent 35%), radial-gradient(circle at 85% 80%, hsl(var(--primary-glow)) 0, transparent 40%)" }} />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-2xl">
                    <SectionLabel className="text-primary-foreground/60">By the numbers</SectionLabel>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight text-balance">The landscape of<span className="italic"> unclaimed money</span> in America.</h2>
                </div>
                <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {STATS.map((s) => (
                        <div key={s.label} className="border-l border-primary-foreground/20 pl-5">
                            <p className="font-serif text-5xl lg:text-6xl tracking-tight">{s.value}</p>
                            <p className="mt-2 text-sm text-primary-foreground/70 leading-snug">{s.label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function TrustSection() {
    const dos = [
        "Real federal & state grant programs",
        "Public unclaimed-property registries",
        "Prior-year tax credits you may have missed",
        "Active class action settlements",
    ];
    const donts = [
        '"Strawman" birth-certificate trust claims',
        '1099-OID redemption filings',
        "UCC-1 personal bond schemes",
        "Anything that would draw IRS fraud penalties",
    ];
    return (
        <section className="relative py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5">
                    <SectionLabel>What we will not do</SectionLabel>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-balance">Money you're <span className="italic text-primary">actually owed</span> — never schemes.</h2>
                    <p className="mt-5 text-base text-muted-foreground leading-relaxed">
                        You may have heard of a “trust set up at birth with your SSN,” “redemption,” or “strawman” filings. These are <span className="font-medium text-foreground">not real</span>, and filing that paperwork results in IRS fraud penalties and, in many cases, criminal charges.
                    </p>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                        Owed only points you toward <span className="font-medium text-foreground">legitimate, documented programs</span>. That's the only way recovery actually lands in your bank account.
                    </p>
                    <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-soft">
                        <ShieldCheck className="h-4 w-4 text-success" />
                        <span className="text-sm">Reviewed by licensed CPAs & enrolled agents</span>
                    </div>
                </div>
                <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                    <Card className="p-6 border-success/30 bg-success/5">
                        <div className="flex items-center gap-2 text-success">
                            <CheckCircle2 className="h-5 w-5" />
                            <p className="font-mono text-xs uppercase tracking-[0.18em]">We help with</p>
                        </div>
                        <ul className="mt-4 space-y-3">
                            {dos.map((d) => (
                                <li key={d} className="flex items-start gap-2 text-sm leading-snug">
                                    <Check className="h-4 w-4 text-success mt-0.5 shrink-0" /> <span>{d}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                    <Card className="p-6 border-destructive/20 bg-destructive/5">
                        <div className="flex items-center gap-2 text-destructive">
                            <XCircle className="h-5 w-5" />
                            <p className="font-mono text-xs uppercase tracking-[0.18em]">We never touch</p>
                        </div>
                        <ul className="mt-4 space-y-3">
                            {donts.map((d) => (
                                <li key={d} className="flex items-start gap-2 text-sm leading-snug">
                                    <Plus className="h-4 w-4 text-destructive mt-0.5 shrink-0 rotate-45" /> <span>{d}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
            </div>
        </section>
    );
}

function Testimonials() {
    return (
        <section id="stories" className="relative py-20 lg:py-28 bg-secondary/40 border-y border-border">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-2xl">
                    <SectionLabel>Stories</SectionLabel>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-balance">People who <span className="italic text-primary">actually got paid.</span></h2>
                </div>
                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {TESTIMONIALS.map((t, i) => (
                        <figure key={i} className="flex flex-col h-full rounded-2xl bg-card border border-border p-6 shadow-soft">
                            <div className="flex items-center gap-1 text-accent">{[0,1,2,3,4].map(k => <Star key={k} className="h-3.5 w-3.5 fill-current" />)}</div>
                            <blockquote className="mt-4 font-serif text-lg leading-snug text-balance">“{t.quote}”</blockquote>
                            <figcaption className="mt-auto pt-5 flex items-end justify-between border-t border-border mt-auto">
                                <div>
                                    <p className="text-sm font-medium">{t.name}</p>
                                    <p className="text-xs text-muted-foreground">{t.role}</p>
                                </div>
                                <p className="font-serif text-2xl text-primary">{t.amount}</p>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FAQSection() {
    return (
        <section id="faq" className="py-20 lg:py-28">
            <div className="mx-auto max-w-4xl px-5 sm:px-8">
                <div className="text-center">
                    <SectionLabel align="center">Questions</SectionLabel>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">The honest answers.</h2>
                </div>
                <Accordion type="single" collapsible className="mt-12 w-full" defaultValue="item-3">
                    {FAQ_ITEMS.map((item, i) => (
                        <AccordionItem key={i} value={`item-${i}`} className="border-border">
                            <AccordionTrigger className="font-serif text-lg sm:text-xl text-left py-6 hover:no-underline">
                                {item.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground leading-relaxed text-base pb-6">
                                {item.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="py-20 lg:py-28">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <div className="relative overflow-hidden rounded-3xl bg-gradient-primary p-10 sm:p-16 text-primary-foreground shadow-elegant">
                    <div aria-hidden className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-accent/30 blur-3xl" />
                    <div aria-hidden className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-primary-glow/30 blur-3xl" />
                    <div className="relative max-w-2xl">
                        <SectionLabel className="text-primary-foreground/60">The quiet path</SectionLabel>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
                            Start your free scan.
                            <br />
                            <span className="italic">See what's already yours.</span>
                        </h2>
                        <p className="mt-5 text-base text-primary-foreground/80 max-w-xl leading-relaxed">
                            Three minutes. No credit card. No obligations. You'll walk away with a prioritized list of real programs and a clear map for filing.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-7 shadow-gold">
                                <Link to="/onboarding">Begin free scan <ArrowRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground">
                                <Link to="/demo">Peek at a demo</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
